import { createModal } from 'components/Modals/ModalFactory'
import { ModalWrapper } from 'components/Modals/ModalWrapper'
import Translation from 'components/Translation'
import { AuthContext, useAuth } from 'context/AuthContext'
import { XIcon } from '@heroicons/react/solid'
import { getTranslation } from 'utils/language-helper'
import {
    DEFAULT_ERROR_STATE,
    IError,
    useAppContext,
} from 'context/AppContext'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { postApiRequest, useApiRequest } from 'utils/fetch-helper'
import AlertPrompt from 'components/AlertPrompt'
import GenericError from 'components/GenericError/GenericError'
import getConfig from 'next/config'

const ModalProvider = createModal(
    AuthContext,
    'StaffConnectCalendarModal',
    () => <></>,
    () => <></>
)

export const StaffConnectCalendarModalOpener = ModalProvider.Opener
export const StaffConnectCalendarModalCloser = ModalProvider.Closer

export type CalendarIntegration = {
    name: string
    image: string
    handleConnect: () => void
    handleDisconnect: () => void
    coming_soon: boolean
    is_connected: boolean
}

function StaffConnectCalendarModal() {
    const router = useRouter()
    const { getApiRequest } = useApiRequest()
    const {
        StaffConnectCalendarModal,
        user,
        is_auth_google,
        getIsAuthGoogle,
    } = useAuth()
    const { attributes, setAttributes } = StaffConnectCalendarModal
    const { translations, setGlobalLoading } = useAppContext()
    const [enable_two_way_sync, toggleEnableTwoWaySync] =
        useState<boolean>(false)
    const [enable_buffer_time, toggleEnableBufferTime] =
        useState<boolean>(false)
    const [is_loading, toggleLoading] = useState<boolean>(false)
    const [error, setError] = useState<IError>(DEFAULT_ERROR_STATE)
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    const handleCloseClick = () => {
        StaffConnectCalendarModal.setAttributes({})
        StaffConnectCalendarModal.close()
    }

    async function handleGoogleConnect() {
        let res = await getApiRequest(
            `/v1/google-calendar/auth-url`,
            undefined,
            true
        )
        window.open(res, '_self')
    }

    async function handleGoogleDisconnect() {
        try {
            let res = await postApiRequest('/v1/google-calendar/disconnect', undefined, undefined, true)
            if (res?.ok) {
                getIsAuthGoogle()
            } else {
                throw res
            }
        } catch (e) {
            setError({ tag: e?.tag, message: e?.message } as IError)
        } finally {
            setGlobalLoading(false)
        }
    }

    const handleOutlookConnect = () => {
        console.log('outlook connect')
    }

    const handleOutlookDisconnect = () => {
        console.log('outlook disconnect')
    }

    useEffect(() => {
        if (!attributes?.calendar_integrations) {
            setAttributes({
                ...attributes,
                calendar_integrations: [
                    {
                        name: getTranslation(
                            'common.google_calendar',
                            translations
                        ),
                        image: `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/logos/google_calendar_logo.png`,
                        handleConnect: handleGoogleConnect,
                        handleDisconnect: handleGoogleDisconnect,
                        coming_soon: false,
                        is_connected:
                            is_auth_google != undefined
                                ? is_auth_google
                                : false,
                    },
                    {
                        name: getTranslation(
                            'common.microsoft_outlook',
                            translations
                        ),
                        image: `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/logos/microsoft-outlook-logo.svg`,
                        handleConnect: handleOutlookConnect,
                        handleDisconnect: handleOutlookDisconnect,
                        coming_soon: true,
                        is_connected: false,
                    },
                ],
            })
            if (is_auth_google == undefined) {
                getIsAuthGoogle()
            }
        }
    }, [attributes?.calendar_integrations])

    useEffect(() => {
        try {
            if (
                router.query.code &&
                router.query.state &&
                !attributes?.show_connected_success
            ) {
                ;(async () => {
                    let res = await postApiRequest(
                        `/v1/google-calendar/authorize?code=${router.query.code}&state=${router.query.state}`
                    )
                    if (res?.ok) {
                        res = await postApiRequest(
                            '/v1/google-calendar/sync-schedules'
                        )
                        if (res?.ok) {
                            setAttributes({
                                ...attributes,
                                show_connected_success: true,
                                calendar_integration: (
                                    attributes?.calendar_integrations as CalendarIntegration[]
                                )[0],
                            })
                            getIsAuthGoogle()
                        } else {
                            throw res
                        }
                    } else {
                        throw res
                    }
                })()
            }
        } catch (e) {
            setError({ tag: e?.tag, message: e?.message } as IError)
        }
    }, [router.query.code, router.query.scope])

    useEffect(() => {
        if (
            is_auth_google != undefined &&
            attributes?.calendar_integrations
        ) {
            ;(
                attributes.calendar_integrations as CalendarIntegration[]
            )[0] = {
                ...(
                    attributes.calendar_integrations as CalendarIntegration[]
                )[0],
                is_connected: is_auth_google,
            }
            setAttributes({
                ...attributes,
                calendar_integrations: attributes.calendar_integrations,
            })
        }
    }, [is_auth_google])

    // TODO: revert height back to 620px when more calendar integrations are added
    return (
        <ModalProvider.Visible>
            <ModalWrapper className="xl:w-wider">
                <div className="bg-white w-[700px] h-[520px] shadow-3xl overflow-x-hidden overflow-y-auto sm:rounded-md m-auto relative border-2 border-gray-100 flex flex-col">
                    <div className="flex px-8 text-gray-500 z-10 pt-4 bg-opacity-30 pb-3 bg-primary-lighter">
                        <div>
                            <ul className="text-xs flex gap-1 breadcrumbs text-gray-400">
                                <Translation
                                    render_as="li"
                                    content_key="dashboard"
                                />
                                <Translation
                                    render_as="li"
                                    content_key="staff"
                                />
                            </ul>
                            <span className="inline-block self-center text-lg text-primary-dark">
                                <Translation
                                    render_as="h2"
                                    content_key="connect_calendar"
                                    className="circular inline"
                                />
                            </span>
                            <span
                                className="text-sm inline-block w-5 h-5 text-center font-extralight absolute right-4 top-6 text-sec-blue-gray  mr-3 cursor-pointer"
                                onClick={handleCloseClick}
                                style={{ color: 'rgb(35, 71, 107)' }}
                            >
                                <XIcon />
                            </span>
                        </div>
                    </div>
                    <div className="p-8 w-full h-full">
                        {/* <div> TODO: comment out once two way sync endpoint is created
                            <Translation
                                render_as="h4"
                                content_key="common.set_applicable_settings"
                                className="text-zinc-600 mb-8 text-sm"
                            />
                            <div> 
                                <div className="flex">
                                    <input
                                        type={"checkbox"}
                                        checked={enable_two_way_sync}
                                        onChange={() => { toggleEnableTwoWaySync(!enable_two_way_sync) }}
                                        className="h-7 w-7 border-gray-300 rounded focus:ring-primary-light text-primary mr-3"
                                    />
                                    <Translation
                                        render_as="label"
                                        content_key="common.enable_two_way_sync"
                                        className="my-auto text-neutral-700 text-sm"
                                    />
                                </div>
                                <div className="flex mt-4">
                                    <input
                                        type={"checkbox"}
                                        checked={enable_buffer_time}
                                        onChange={() => { toggleEnableBufferTime(!enable_buffer_time) }}
                                        className="h-7 w-7 border-gray-300 rounded focus:ring-primary-light text-primary mr-3"
                                    />
                                    <Translation
                                        render_as="label"
                                        content_key="common.include_before_after_buffer_time"
                                        className="my-auto text-neutral-700 text-sm"
                                    />
                                </div>
                            </div>
                        </div> */}
                        <div className="w-full p-4 mt-8">
                            <Translation
                                content_key="common.connect_to"
                                render_as="label"
                                className="text-primary-dark w-[40%] inline-block uppercase text-xs font-bold"
                            />
                            <Translation
                                content_key="common.linked_account"
                                render_as="label"
                                className="text-primary-dark w-[30%] inline-block uppercase text-xs font-bold"
                            />
                        </div>
                        <div className="border border-gray-200 w-full h-0" />
                        {attributes?.calendar_integrations &&
                            (
                                attributes?.calendar_integrations as CalendarIntegration[]
                            )?.map((integration: CalendarIntegration, idx: number) => {
                                return (
                                    <div key={idx}>
                                        <div className="w-full p-4 flex flex-row">
                                            <div className="w-[40%] flex flex-row">
                                                <div className="h-10 w-10 bg-blue-50 rounded-md flex justify-center mr-3">
                                                    <img
                                                        src={
                                                            integration.image
                                                        }
                                                        className="w-7 h-7 m-auto"
                                                        alt={
                                                            integration.name
                                                        }
                                                    />
                                                </div>
                                                <label className="flex font-[450] text-base text-black items-center">
                                                    {integration.name}
                                                </label>
                                            </div>
                                            <div className="w-[30%] flex">
                                                {integration.is_connected ? (
                                                    <label className="flex items-center text-black text-base font-[450]">
                                                        {user?.email}
                                                    </label>
                                                ) : (
                                                    <Translation
                                                        content_key="common.no_linked_account"
                                                        render_as="label"
                                                        className="flex items-center text-gray-400 text-base font-[450]"
                                                    />
                                                )}
                                            </div>
                                            <div className="w-[30%] flex">
                                                {integration.coming_soon ? (
                                                    <button className="rounded-lg uppercase bg-gradient-to-r from-pink-500 to-sky-500 text-white w-32 h-10 m-auto font-medium text-sm">
                                                        <Translation content_key="coming_soon" />
                                                    </button>
                                                ) : integration.is_connected ? (
                                                    <button
                                                        className="rounded-lg bg-white text-gray-600 w-32 h-10 m-auto text-sm font-medium border border-gray-400"
                                                        onClick={() => {
                                                            setAttributes({
                                                                ...attributes,
                                                                calendar_integration:
                                                                    integration,
                                                                show_disconnect_prompt:
                                                                    true,
                                                            })
                                                        }}
                                                    >
                                                        <Translation content_key="common.disconnect" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="rounded-lg bg-primary-light text-white w-32 h-10 m-auto text-sm font-medium"
                                                        onClick={
                                                            integration.handleConnect
                                                        }
                                                    >
                                                        <Translation content_key="common.connect_now" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="border border-gray-200 w-full h-0" />
                                    </div>
                                )
                            })}
                    </div>
                </div>
            </ModalWrapper>
            <AlertPrompt
                title="staff.successfully_connected_calendar"
                body=""
                show={!!attributes?.show_connected_success}
                className="absolute top-0 flex flex-col items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm w-full h-full z-30"
                logo={
                    <div className="w-full h-[70px] flex justify-center">
                        <div className="w-24 rounded-md flex justify-center relative">
                            <i className="feather-check bg-green-200 w-8 h-8 text-green-500 rounded-full py-2 px-2 block absolute -top-1.5 right-1" />
                            <img
                                src={
                                    (
                                        attributes?.calendar_integration as CalendarIntegration
                                    )?.image
                                }
                                className="w-14 h-14 m-auto"
                                alt={
                                    (
                                        attributes?.calendar_integration as CalendarIntegration
                                    )?.name
                                }
                            />
                        </div>
                    </div>
                }
                handlebars={
                    !!attributes?.calendar_integration
                        ? {
                              calendar_name: (
                                  attributes?.calendar_integration as CalendarIntegration
                              ).name as string,
                          }
                        : {}
                }
                actions={[
                    <button
                        key="back_button"
                        type="button"
                        className="rounded-lg h-10 px-6 bg-primary text-white"
                        onClick={() => {
                            delete attributes?.calendar_integration,
                                setAttributes({
                                    ...attributes,
                                    show_connected_success: false,
                                })
                        }}
                    >
                        <Translation content_key="staff.back_to_connect_calendar" />
                    </button>,
                ]}
            />
            <AlertPrompt
                title="staff.disconnect_calendar"
                show={!!attributes?.show_disconnect_prompt}
                className="absolute top-0 flex flex-col items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm w-full h-full z-[101]"
                logo={
                    <div className="w-full h-[70px] flex justify-center">
                        <div className="w-24 rounded-md flex justify-center relative">
                            <i className="feather-x bg-red-100 text-red-500 w-8 h-8 rounded-full py-2 px-2 block absolute -top-1 right-2" />
                            <img
                                src={
                                    (
                                        attributes?.calendar_integration as CalendarIntegration
                                    )?.image
                                }
                                className="w-14 h-14 m-auto"
                                alt={
                                    (
                                        attributes?.calendar_integration as CalendarIntegration
                                    )?.name
                                }
                            />
                        </div>
                    </div>
                }
                handlebars={
                    !!attributes?.calendar_integration
                        ? {
                              calendar_name: (
                                  attributes?.calendar_integration as CalendarIntegration
                              ).name as string,
                          }
                        : {}
                }
                actions={[
                    <button
                        key="back_button"
                        type="button"
                        className="rounded-lg h-10 px-6 border-slate-800/30 border"
                        onClick={() => {
                            setAttributes({
                                ...attributes,
                                show_disconnect_prompt: false,
                            })
                        }}
                    >
                        <Translation content_key="cancel" />
                    </button>,
                    <button
                        key="disconnect_button"
                        type="button"
                        className={`transition-all rounded-lg h-10 px-6 bg-primary${
                            is_loading ? '/20' : ''
                        } text-white`}
                        disabled={is_loading}
                        onClick={async () => {
                            setGlobalLoading(true)
                            delete attributes?.to_disconnect
                            toggleLoading(true)
                            try {
                                ;(
                                    attributes?.calendar_integration as CalendarIntegration
                                ).handleDisconnect()
                                delete attributes?.calendar_integration
                                setAttributes({
                                    ...attributes,
                                    show_disconnect_prompt: false,
                                })
                            } catch (e) {
                                console.error(e)
                            } finally {
                                toggleLoading(false)
                            }
                        }}
                    >
                        <Translation content_key="disconnect" />
                    </button>,
                ]}
            />
            {error?.message?.length > 0 ? (
                <GenericError
                    error_message={error?.message}
                    onCloseModal={() => {
                        setError(DEFAULT_ERROR_STATE)
                    }}
                    tag={error?.tag}
                />
            ) : (
                <></>
            )}
        </ModalProvider.Visible>
    )
}

export default StaffConnectCalendarModal
